import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismCard,
  PrismIcon,
  PrismMetricBlock,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import GeneralChartsChartUpdater from 'lumen/components/general/charts/chart-updater';
import HotelMarketInsightChartsChartDemandLevelBuckets from 'lumen/components/hotel/market-insight/charts/chart-demand-level-buckets';
import type { Day, Rateindex } from 'lumen/models';

export type RateIndexesForCurrentSelectionPerSubmarketLevelBucket = {
  bucketBoundsPriceCurrency: string;
  bucketLowerBound: number;
  bucketLowerBoundPrice: null;
  bucketUpperBound: number;
  bucketUpperBoundPrice: number;
  containsOwnHotel: boolean;
  hotelNames: string[];
  rateIndexes: Rateindex[];
};

interface HotelMarketInsightCardsPriceLevelDistributionSignature {
  Args: {
    currentExtractDate: Dayjs | string;
    detailSectionId: string;
    isLoadingSummariesOrDetail: boolean;
    rateIndexesForCurrentSelectionPerSubmarketLevelBucket: RateIndexesForCurrentSelectionPerSubmarketLevelBucket;
    selectedDayModel: Day;
    setFocusedDetailSection: (detailSectionId: string) => void;
  };
}

export default class HotelMarketInsightCardsPriceLevelDistribution extends Component<HotelMarketInsightCardsPriceLevelDistributionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare miRateindex: Services['mi-rateindex'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get firstOutlierTitle() {
    return this.args.selectedDayModel.outliers[0]?.formattedValueString.short;
  }

  get rateIndexDataOfOwnHotel(): Rateindex | null {
    // No requests loading -> all data should be in the store
    if (!this.args.isLoadingSummariesOrDetail) {
      return this.miRateindex.getRateIndexForCurrentHotelAndExtractDate(
        this.args.selectedDayModel,
        this.args.currentExtractDate,
      );
    }

    // When we've just changed date the currentExtractDate will still be null (waits for the detailRequest)
    // For the own hotel we already have the rate index for one extract date. Try to show this directly
    if (!this.args.currentExtractDate && this.args.selectedDayModel) {
      const selectedDay = this.args.selectedDayModel;

      const rateIndexForCurrentHotelAndLatestExtractDate =
        this.miRateindex.getRateIndexForCurrentHotelAndLatestExtractDate(
          selectedDay,
        );

      // Extra check to make sure it's an actual rateIndex object retrieved from the store
      if (
        rateIndexForCurrentHotelAndLatestExtractDate &&
        rateIndexForCurrentHotelAndLatestExtractDate.id
      ) {
        return rateIndexForCurrentHotelAndLatestExtractDate;
      }
    }

    // In case the above didn't return any decent result - have a fallback loading state
    // Note: used to be an object with separate loading state values. Think this fallback loading state was not used anymore since layout v2
    return null;
  }

  @action
  viewPriceLevelDistributionButtonClicked(): void {
    const day = this.args.selectedDayModel.date;
    const { outliers } = this.args.selectedDayModel;

    this.usageAnalytics.track('Price Level Distribution Viewed', {
      eventTrigger: 'day-detail-see-more',
      daysOffsetFromToday: day
        .startOf('day')
        .diff(this.hotelAndMarketService.getLocalTime().startOf('day'), 'days'),
      isOutlier: !isEmpty(outliers),
    });

    this.args.setFocusedDetailSection(this.args.detailSectionId);
  }

  <template>
    <GeneralChartsChartUpdater as |chartUpdater|>
      <PrismCard
        class="detail-pane-card ic-product-tour-price-level-distribution-card"
      >
        <:header as |Header|>
          <div class="flex items-center gap-100">
            <Header.title>
              {{t "market_insight.price_level_distribution"}}
            </Header.title>

            <PrismIcon @name="circle-information-outline" @size="extra-small" />
            <EmberTooltip @side="right" @tooltipClass="info-tooltip">
              <p class="text-200--bold">{{t
                  "market_insight.price_level_distribution"
                }}</p>
              <p class="paragraph-200--regular">{{t
                  "market_insight.price_level_distribution.tooltip"
                }}</p>
              &nbsp;
              <p class="text-200--bold">{{t "market_insight.usual_price"}}</p>
              <p class="paragraph-200--regular">{{t
                  "market_insight.price_level_distribution.tooltip.dow_price"
                }}</p>
            </EmberTooltip>
          </div>
        </:header>

        <:headerAfter>
          <button
            class="back-button button--unstyled ic-product-tour-price-level-distribution-see-details flex flex-row items-center"
            type="button"
            {{on "click" this.viewPriceLevelDistributionButtonClicked}}
          >
            <p class="text-200--regular mr-100">{{t "general.see_details"}}</p>
            <PrismIcon @name="arrow-right" @size="small" />
          </button>
        </:headerAfter>

        <:content>
          {{#if this.firstOutlierTitle}}
            <div class="mb-200">
              <PrismBanner
                @bannerMessage=""
                @bannerStyle="warning"
                @bannerTitle={{this.firstOutlierTitle}}
              />
            </div>
          {{/if}}

          <PrismMetricBlock @align="left" @compact={{true}}>
            <:value>
              <span class="text-400--bold">
                <div class="flex flex-row items-center gap-100">
                  {{#if this.rateIndexDataOfOwnHotel}}
                    {{this.rateIndexDataOfOwnHotel.formattedRate}}

                    {{#if
                      this.rateIndexDataOfOwnHotel.canShowFormattedCancellableText
                    }}
                      <PrismTooltip
                        @text={{this.rateIndexDataOfOwnHotel.formattedRoomNameAndCancellableText}}
                      />
                    {{/if}}

                    {{#if
                      this.rateIndexDataOfOwnHotel.formattedCancellableIcon
                    }}
                      <PrismIcon
                        @name={{this.rateIndexDataOfOwnHotel.formattedCancellableIcon}}
                        @size="small"
                      />
                    {{/if}}
                  {{else}}
                    {{t "general.missing"}}
                  {{/if}}
                </div>
              </span>
            </:value>
            <:label>{{t "market_insight.my_price"}}</:label>
          </PrismMetricBlock>

          <HotelMarketInsightChartsChartDemandLevelBuckets
            {{! @glint-expect-error: Incorrect type }}
            @isLoading={{@isLoadingSummariesOrDetail}}
            @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
            @rateIndexesForCurrentSelectionPerSubmarketLevelBucket={{@rateIndexesForCurrentSelectionPerSubmarketLevelBucket}}
            @selectedDayModel={{@selectedDayModel}}
          />
        </:content>
      </PrismCard>
    </GeneralChartsChartUpdater>
  </template>
}
